import { CommonModule } from '@angular/common';
import { Component, OnInit, input, output, signal } from '@angular/core';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { HeadingComponent } from '../../../../shared/components/heading/heading.component';
import { ReviewerService } from '../../../../shared/services/reviewer.service';
import { Reviewer } from '../../../../store/reviewer/reviewer.interface';
import { Criteria, SelectedCriteries, SkillsCriteria, ReviewerCriteriaObject } from '../profile.interface';
import { FormsModule } from '@angular/forms';
import {ToastService} from '../../../../shared/services/toast.service'
import { Router } from '@angular/router';
import { HelperService } from '../../../../shared/services/helper.service';
import { lastValueFrom } from 'rxjs';
import { StorageService } from '../../../../shared/services/storage.service';
import { PipesModule } from '../../../../shared/pipes/pipes.module';
import { CommonBaseClass } from '../../../../shared/classes/common-base-class';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-skills',
  standalone: true,
  imports: [CommonModule, ButtonModule, FormsModule, FloatLabelModule, CalendarModule, InputTextModule, DropdownModule, HeadingComponent, MultiSelectModule, AccordionModule, TagModule, FileUploadModule, ChipModule, PipesModule],
  templateUrl: './skills.component.html',
  styleUrl: './skills.component.scss'
})
export class SkillsComponent extends CommonBaseClass implements OnInit {
  from = input<string>('');
  nextStep = output<void>();
  backStep = output<void>();

  dummy!: any[];
  headingButton = {
    url: '/reviewer/profile/education/create',
    label: 'Add credentials'
  }
  appName: string = environment.title;
  reviewer!: Reviewer;
  skillsCriteriaListing: SkillsCriteria = [];
  userFirmId: string='';
  userFirmName: string='';
  breadcrumbTitleOptions = [
    { label: 'Profile' }, 
    { label: 'Skills & experience' }
  ];
  /**
   * MP-5722: experience-enabled values that were saved without a level, keyed
   * by criteria id and value. Set by validateExperienceLevels(), cleared when
   * the user picks a level or removes the chip.
   */
  readonly experienceLevelErrors = signal<Record<string, boolean>>({});

  constructor(private reviewerService: ReviewerService, private toast:ToastService, private router: Router, helperService: HelperService, private storageService:StorageService) { 
    super(helperService);
  }

  ngOnInit() {
    this.reviewer = this.reviewerService.getReviewer();
    this.userFirmId = this.storageService.getTenant()?.firm?._id;
    this.userFirmName = this.storageService.getTenant()?.firm?.name || '';
    this.getUserSkillsByDynamicCriteria();
  }

  hasExperienceEnabled(options: any[]): boolean {
    return options?.some(o => o.isExperienceEnabled) ?? false;
  }

  getUserSkillsByDynamicCriteria() {
    this.reviewerService.getFilteredCriteriaBasedUserSkills(this.reviewer._id).subscribe((resp: any) => {
      this.reviewerService.getReviewerCriteria(this.reviewer._id).subscribe((data: SelectedCriteries) => {
        let reviewerCriteriaObject: ReviewerCriteriaObject = {};
        if (data.length > 0) {
          reviewerCriteriaObject = data.reduce((obj, item) => {
            let wrap = {
              [item.criteriaName]: item
            };
            Object.assign(obj, wrap);
            return obj;
          }, {});
        }
        resp.map((d: Criteria) => {
          const name = d.name;
          d.criteriaSelectedOption = reviewerCriteriaObject[name.toString()] ? reviewerCriteriaObject[name.toString()].criteriaSelectedOption : [];
          if (d.label?.values && d.label?.values?.length) {
            d.label.values = d.label.values.slice().sort((a: any, b: any) => a.val.localeCompare(b.val));
          }
          d.criteriaSelectedOption = this.getCriteriaOptions(d);
        })
        this.skillsCriteriaListing = resp;
      })
    })
  }

  async updateProfileStrengthByTenant(){
    lastValueFrom(this.reviewerService.updateProfileStrengthByTenant(this.userFirmId,{skill:true}));
  }
  getCriteriaOptions(d: any){
    const options = d.criteriaSelectedOption.map((option: any) => {
      // Find the reference from label.values
      const filterData = d.label && d.label.values && d.label.values.find((value: any) => value.val === option.val);
      
      // Find the reference from experienceLevels if experience is enabled
      const experienceLevel = option.isExperienceEnabled 
        ? d.experienceLevels.find((exp: any) => exp.experience === option.selectedExperienceLevel?.experience) 
        : null;
    
      // If a match is found, return the original reference
      return filterData 
        ? Object.assign(filterData, { selectedExperienceLevel: experienceLevel || null }) 
        : null;
    }).filter((item: any) => item !== null);
    return options;
  }

  onRemoveChip(data: any, index: number) {
    const criteria: any = this.skillsCriteriaListing[index];
    const option = criteria.criteriaSelectedOption.filter((d: any) => {
      return d.val !== data.val;
    })
    criteria.criteriaSelectedOption = option;
    this.clearExperienceLevelError(criteria, data);
  }

  experienceLevelKey(criteria: any, option: any): string {
    return `${criteria?._id ?? criteria?.name}::${option?.val}`;
  }

  hasExperienceLevelError(criteria: any, option: any): boolean {
    return this.experienceLevelErrors()[this.experienceLevelKey(criteria, option)] === true;
  }

  onExperienceLevelChange(criteria: any, option: any): void {
    this.clearExperienceLevelError(criteria, option);
  }

  /**
   * MP-5722: an experience-enabled value must carry a chosen level. Nothing is
   * defaulted any more; the old `{experience: "", priority: "0"}` fallback is
   * what wrote the null / "0" priorities that drop users from experience
   * filtered searches.
   */
  validateExperienceLevels(): boolean {
    const errors: Record<string, boolean> = {};
    this.skillsCriteriaListing.forEach((criteria: any) => {
      (criteria.criteriaSelectedOption || []).forEach((option: any) => {
        if (option.isExperienceEnabled && !option.selectedExperienceLevel?.experience) {
          errors[this.experienceLevelKey(criteria, option)] = true;
        }
      });
    });
    this.experienceLevelErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  save(){
    if (!this.validateExperienceLevels()) {
      this.toast.showError('Required fields missing.');
      return;
    }
    let criterias:any = {};
    this.skillsCriteriaListing.map((d:any)=>{
      if(!criterias[d.name]){
        const jobCategory = d.jobCategory ? (typeof d.jobCategory === 'string' ? [d.jobCategory] : d.jobCategory.map((category: any) => category._id || category)) : [];
        criterias[d.name] = {selectedOptions : d.criteriaSelectedOption, jobCategory, criteriaId: d._id};
      }
    })
    const payload = {
      criterias: criterias,
      reviewerId: this.reviewer._id
    }
    this.reviewerService.saveCriteriaReviewer(payload).subscribe((d:any)=>{
      this.toast.showSuccess('Skills and Experience Updated successfully');
      if (this.from() === 'onboarding' && this.isPWCEnabled) {
        this.nextStep.emit();
      } else {
        this.onBack();
      }
    });
  }

  onBack(): void {
    if (this.from() === 'onboarding') {
      this.backStep.emit();
    } else {
      this.navigateToMainProfile();
    }
  }

  hideKeyboard(): void {
      setTimeout(() => {
        const input = document.querySelector('input.p-multiselect-filter') as HTMLInputElement;
        if (input) {
            input.blur();
        }
      });
  }

  onMultiSelectPanelShow(){
    this.updateProfileStrengthByTenant();
  }

  private clearExperienceLevelError(criteria: any, option: any): void {
    const key = this.experienceLevelKey(criteria, option);
    if (this.experienceLevelErrors()[key]) {
      this.experienceLevelErrors.update((errors) => {
        const next = { ...errors };
        delete next[key];
        return next;
      });
    }
  }
}
